import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Users, Building2, UserCheck, ChevronDown, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export type VisibilityLevel = 'private' | 'team' | 'org' | 'restricted';

export interface VisibilityOption {
  level: VisibilityLevel;
  icon: React.ElementType;
  label: string;
  commandLabel: string;
  description: string;
  commandDescription: string;
}

const VISIBILITY_OPTIONS: VisibilityOption[] = [
  {
    level: 'private',
    icon: Lock,
    label: 'Private',
    commandLabel: 'Classified',
    description: 'Only you can see',
    commandDescription: 'Eyes only - restricted access',
  },
  {
    level: 'team',
    icon: Users,
    label: 'Team',
    commandLabel: 'Squadron',
    description: 'Team members can see',
    commandDescription: 'Squadron-level clearance',
  },
  {
    level: 'org',
    icon: Building2,
    label: 'Organization',
    commandLabel: 'Fleet',
    description: 'Everyone in org can see',
    commandDescription: 'Fleet-wide broadcast',
  },
  {
    level: 'restricted',
    icon: UserCheck,
    label: 'Restricted',
    commandLabel: 'Need-to-Know',
    description: 'Only specific people',
    commandDescription: 'Explicit clearance required',
  },
];

interface VisibilityIndicatorProps {
  level: VisibilityLevel;
  editable?: boolean;
  onChange?: (level: VisibilityLevel) => void;
  onRestrictedClick?: () => void;
  showLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function VisibilityIndicator({
  level,
  editable = false,
  onChange,
  onRestrictedClick,
  showLabel = true,
  size = 'md',
  className = '',
}: VisibilityIndicatorProps) {
  const { isCommand } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption = VISIBILITY_OPTIONS.find((opt) => opt.level === level) || VISIBILITY_OPTIONS[0];
  const Icon = currentOption.icon;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (option: VisibilityOption) => {
    if (option.level === 'restricted' && onRestrictedClick) {
      onRestrictedClick();
    }
    onChange?.(option.level);
    setIsOpen(false);
  };

  const getVisibilityColor = (visLevel: VisibilityLevel) => {
    switch (visLevel) {
      case 'private':
        return isCommand ? 'text-danger-400' : 'text-gray-400';
      case 'team':
        return isCommand ? 'text-primary-400' : 'text-blue-500';
      case 'org':
        return isCommand ? 'text-warning-400' : 'text-green-500';
      case 'restricted':
        return isCommand ? 'text-purple-400' : 'text-orange-500';
    }
  };

  const sizeClasses = {
    sm: {
      icon: 'w-3 h-3',
      text: 'text-[10px]',
      padding: 'px-1.5 py-0.5',
      gap: 'gap-1',
    },
    md: {
      icon: 'w-4 h-4',
      text: 'text-xs',
      padding: 'px-2 py-1',
      gap: 'gap-1.5',
    },
  };

  const sizes = sizeClasses[size];

  // Badge-only mode (non-editable)
  if (!editable) {
    return (
      <div
        className={`
          inline-flex items-center ${sizes.gap} ${sizes.padding} rounded-full
          ${isCommand ? 'bg-surface-800 border border-surface-600' : 'bg-surface-700/50'}
          ${className}
        `}
        title={isCommand ? currentOption.commandDescription : currentOption.description}
      >
        <Icon className={`${sizes.icon} ${getVisibilityColor(level)}`} />
        {showLabel && (
          <span
            className={`
              ${sizes.text} font-medium ${getVisibilityColor(level)}
              ${isCommand ? 'font-mono uppercase tracking-wider' : ''}
            `}
          >
            {isCommand ? currentOption.commandLabel : currentOption.label}
          </span>
        )}
      </div>
    );
  }

  // Editable dropdown mode
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          inline-flex items-center ${sizes.gap} ${sizes.padding} rounded-full
          transition-all duration-200
          ${
            isCommand
              ? 'bg-surface-800 border border-surface-600 hover:border-primary-500/50 hover:shadow-glow-sm'
              : 'bg-surface-700/50 hover:bg-surface-700 border border-transparent hover:border-surface-500'
          }
        `}
      >
        <Icon className={`${sizes.icon} ${getVisibilityColor(level)}`} />
        {showLabel && (
          <span
            className={`
              ${sizes.text} font-medium ${getVisibilityColor(level)}
              ${isCommand ? 'font-mono uppercase tracking-wider' : ''}
            `}
          >
            {isCommand ? currentOption.commandLabel : currentOption.label}
          </span>
        )}
        <ChevronDown
          className={`
            ${sizes.icon} text-text-muted transition-transform duration-200
            ${isOpen ? 'rotate-180' : ''}
          `}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`
              absolute top-full left-0 mt-1 z-50 w-56
              rounded-lg shadow-xl overflow-hidden
              ${
                isCommand
                  ? 'bg-surface-800 border border-surface-600 shadow-glow-sm'
                  : 'bg-surface-800 border border-surface-600'
              }
            `}
          >
            {VISIBILITY_OPTIONS.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.level === level;

              return (
                <button
                  key={option.level}
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full flex items-start gap-3 px-3 py-2.5 text-left
                    transition-colors duration-150
                    ${
                      isSelected
                        ? isCommand
                          ? 'bg-primary-500/10'
                          : 'bg-primary-500/10'
                        : 'hover:bg-surface-700'
                    }
                  `}
                >
                  <OptionIcon
                    className={`w-4 h-4 mt-0.5 flex-shrink-0 ${getVisibilityColor(option.level)}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span
                        className={`
                          text-sm font-medium text-text-primary
                          ${isCommand ? 'font-mono uppercase tracking-wider text-xs' : ''}
                        `}
                      >
                        {isCommand ? option.commandLabel : option.label}
                      </span>
                      {isSelected && (
                        <Check className={`w-4 h-4 ${getVisibilityColor(option.level)}`} />
                      )}
                    </div>
                    <span
                      className={`
                        text-text-muted block mt-0.5
                        ${isCommand ? 'text-[10px] font-mono' : 'text-xs'}
                      `}
                    >
                      {isCommand ? option.commandDescription : option.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Export visibility options for use elsewhere
export { VISIBILITY_OPTIONS };
